import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import NtText from "./NtText";
import NtTouchableEffect from "./NtTouchableEffect";
import { useTheme } from "../context/ThemeContext";
import { darkTheme, lightTheme } from "../config/themes";

const MODES = [
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
  { value: "system", label: "System" },
];

/**
 * Three-card theme picker. Each card renders a tiny mockup of the actual
 * surface/border tokens for that mode (split for "System"). Tap to switch.
 * Modeled after voiplib-rn's `NtThemePicker`.
 */
function NtThemePicker() {
  const { theme, mode, setMode } = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const tokensFor = (key) => (key === "dark" ? darkTheme : lightTheme);

  return (
    <View style={styles.row}>
      {MODES.map((opt) => {
        const isSelected = mode === opt.value;
        const isSystem = opt.value === "system";
        const tokens = tokensFor(opt.value === "system" ? "dark" : opt.value);

        return (
          <NtTouchableEffect
            key={opt.value}
            style={[
              styles.card,
              {
                borderColor: isSelected ? theme.brand2 : theme.borderSoft,
                backgroundColor: theme.surface,
              },
            ]}
            onPress={() => setMode(opt.value)}
          >
            <View style={[styles.mockup, { backgroundColor: tokens.bg }]}>
              {isSystem ? (
                <View style={styles.split}>
                  <Mock tokens={lightTheme} />
                  <Mock tokens={darkTheme} />
                </View>
              ) : (
                <Mock tokens={tokens} />
              )}
            </View>
            <NtText
              style={[
                styles.label,
                { color: isSelected ? theme.brand2 : theme.textDim },
              ]}
            >
              {opt.label}
            </NtText>
          </NtTouchableEffect>
        );
      })}
    </View>
  );
}

function Mock({ tokens }) {
  return (
    <View style={{ flex: 1, backgroundColor: tokens.bg }}>
      <View
        style={{
          height: "26%",
          backgroundColor: tokens.surface,
          borderBottomWidth: StyleSheet.hairlineWidth,
          borderBottomColor: tokens.borderSoft,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <View
          style={{
            height: 3,
            width: "60%",
            borderRadius: 2,
            backgroundColor: tokens.textDim,
            opacity: 0.45,
          }}
        />
      </View>
      <View style={{ padding: 6, gap: 3 }}>
        <View
          style={{
            height: 3,
            width: "75%",
            borderRadius: 1.5,
            backgroundColor: tokens.textDim,
            opacity: 0.25,
          }}
        />
        <View
          style={{
            height: 3,
            width: "55%",
            borderRadius: 1.5,
            backgroundColor: tokens.textDim,
            opacity: 0.2,
          }}
        />
      </View>
    </View>
  );
}

const makeStyles = (t) =>
  StyleSheet.create({
    row: {
      flexDirection: "row",
      gap: 8,
      alignSelf: "flex-start",
    },
    card: {
      width: 120,
      borderRadius: 8,
      borderWidth: 2,
      overflow: "hidden",
      alignItems: "center",
    },
    mockup: {
      width: "100%",
      aspectRatio: 1.7,
      overflow: "hidden",
    },
    split: {
      flex: 1,
      flexDirection: "row",
    },
    label: {
      fontSize: 11,
      fontWeight: "600",
      paddingVertical: 6,
    },
  });

export default NtThemePicker;
